import AccountMappingApproval from "./AccountMappingApproval";
import DomainSourceUpload from "./DomainSourceUpload";
import { useCallback, useEffect, useState } from "react";
import MonthlyBridge from "./MonthlyBridge";
import api, { rupiah, getErrorMessage, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Receipt, FileDown } from "lucide-react";

const STATUS_STYLE = {
  RECONCILED: "bg-green-100 text-green-800 border-green-300",
  COMPLETE: "bg-green-100 text-green-800 border-green-300",
  APPROVED: "bg-green-100 text-green-800 border-green-300",
  PASS: "bg-green-100 text-green-800 border-green-300",
  PASS_WITH_REVIEW_ITEMS: "bg-amber-100 text-amber-800 border-amber-300",
  REVIEW_REQUIRED: "bg-amber-100 text-amber-800 border-amber-300",
  INCOMPLETE: "bg-red-100 text-red-800 border-red-300",
  AMBIGUOUS: "bg-red-100 text-red-800 border-red-300",
  BLOCKED_SOURCE: "bg-red-100 text-red-800 border-red-300",
  BLOCKED_MAPPING: "bg-red-100 text-red-800 border-red-300",
  BLOCKED_PERIOD: "bg-red-100 text-red-800 border-red-300",
  SOURCE_NOT_SUPPLIED: "bg-slate-100 text-slate-700 border-slate-300",
};
const ACCEPTANCE_LABELS = {
  PASS: "Diterima",
  PASS_WITH_REVIEW_ITEMS: "Diterima dengan pos reviu",
  BLOCKED_SOURCE: "Tertahan — sumber penjualan belum tersedia",
  BLOCKED_MAPPING: "Tertahan — pemetaan akun pendapatan belum disetujui",
  BLOCKED_PERIOD: "Tertahan — periode belum lengkap",
  REVIEW_REQUIRED: "Perlu reviu",
};
const ITEM_LABELS = {
  MATCHED_EXACT: "Cocok tepat",
  MATCHED_ONE_TO_MANY: "Satu ke banyak",
  MATCHED_MANY_TO_ONE: "Banyak ke satu",
  MATCHED_PARTIAL: "Cocok sebagian",
  TIMING_DIFFERENCE: "Perbedaan waktu",
  DATE_DIFFERENCE: "Perbedaan tanggal",
  AMOUNT_DIFFERENCE: "Perbedaan nilai",
  SALES_ONLY: "Hanya di penjualan",
  GL_ONLY: "Hanya di buku besar",
  MATCHED_SALES_VAT: "Cocok dengan PPN keluaran",
  SALES_WITHOUT_VAT: "Penjualan tanpa faktur pajak",
  VAT_WITHOUT_SALES: "Faktur pajak tanpa penjualan",
  VAT_AMOUNT_DIFFERENCE: "Perbedaan nilai PPN",
  VAT_DATE_DIFFERENCE: "Perbedaan tanggal PPN",
  VAT_TIMING_DIFFERENCE: "Perbedaan masa pajak",
  VAT_CUSTOMER_MISMATCH: "Pembeli berbeda",
  VAT_NOT_APPLICABLE: "PPN tidak berlaku",
  MATCHED_SALES_AR: "Cocok dengan piutang",
  FULLY_SETTLED: "Telah dilunasi",
  PARTIALLY_SETTLED: "Dilunasi sebagian",
  AR_ONLY: "Hanya di piutang",
  CUSTOMER_MISMATCH: "Pelanggan berbeda",
  FULLY_SETTLED_OR_CASH_SALE_CANDIDATE: "Kandidat lunas / penjualan tunai",
  REVIEW_REQUIRED: "Perlu reviu",
};
const NOT_AVAILABLE = "Tidak berlaku / sumber tidak tersedia";

const TABS = [
  ["ringkasan", "Ringkasan", () => true],
  ["populasi", "Populasi Penjualan", (v) => v.population?.controls?.rows > 0],
  ["gl", "Revenue GL", (v) => !!v.sales_vs_gl],
  ["vsgl", "Penjualan vs GL", (v) => !!v.sales_vs_gl],
  ["vsvat", "Penjualan vs PPN Keluaran", (v) => !!v.sales_vs_vat],
  ["vsar", "Penjualan vs Piutang", (v) => !!v.sales_vs_ar],
  ["pos", "Pos Rekonsiliasi", (v) => !!v.sales_vs_gl],
  ["bulanan", "Bulanan → Tahunan", (v) => !!v.sales_vs_gl],
  ["cutoff", "Cut-off", () => true],
  ["retur", "Retur / Diskon", (v) => v.returns?.status === "PRESENT"],
  ["bukti", "Bukti Audit", () => true],
];

// Penjualan / Pendapatan — satu populasi audit untuk periode audit yang dideklarasikan.
export default function SalesRevenue({ id }) {
  const [view, setView] = useState(null);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("ringkasan");

  const load = useCallback(() => {
    api.get(`/engagements/${id}/sales`)
      .then(({ data }) => setView(data))
      .catch((e) => toast.error(getErrorMessage(e)));
    api.get(`/engagements/${id}/sales-summary`)
      .then(({ data }) => setSummary(data))
      .catch(() => setSummary(null));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const exportWorkpaper = () =>
    downloadFile(`/engagements/${id}/sales/workpaper.xlsx`, `kertas-kerja-penjualan-${id}.xlsx`)
      .catch((e) => toast.error(getErrorMessage(e)));

  if (!view) return <div className="text-xs text-muted-foreground p-4">Memuat data penjualan…</div>;
  const controls = view.population?.controls || {};
  const glSummary = view.sales_vs_gl?.summary;
  const available = TABS.filter(([, , when]) => when(view));

  return (
    <div className="space-y-4" data-testid="sales-revenue">
      <DomainSourceUpload id={id} endpoint="sales-sources" label="Unggah sumber penjualan (rincian penjualan, PPN keluaran A2, umur piutang, penerimaan setelah neraca)" accept=".xlsx,.xls,.csv" onUploaded={load} />
      <AccountMappingApproval id={id} path="revenue-accounts" title="Pemetaan akun pendapatan" testid="sales-mapping-approval" onApproved={load}
        groups={[{ key: "revenue_accounts", candidates: "revenue_candidates", label: "Akun pendapatan", required: true },
                 { key: "contra_accounts", candidates: "contra_candidates", label: "Akun pengurang pendapatan (retur / diskon)" },
                 { key: "receivable_accounts", candidates: "receivable_candidates", label: "Akun piutang usaha" }]} />
      <div className="bg-white border border-border rounded-sm p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 font-head font-semibold text-sm mr-2">
          <Receipt size={15} />Penjualan / Pendapatan
        </div>
        <div className="text-xs text-muted-foreground">{view.audit_period_display}</div>
        <Button size="sm" variant="outline" className="ml-auto" onClick={exportWorkpaper}
          data-testid="sales-export">
          <FileDown size={14} className="mr-1" />Ekspor Kertas Kerja
        </Button>
      </div>

      {summary && (
        <div className="bg-white border border-border rounded-sm p-2" data-testid="sales-domain-summary">
          <div className="text-xs font-head font-semibold px-1 pb-2">
            Ringkasan Domain Penjualan
            <span className={`ml-2 font-normal inline-block px-2 py-0.5 border rounded-sm ${STATUS_STYLE[summary.acceptance] || ""}`}>
              {ACCEPTANCE_LABELS[summary.acceptance] || summary.acceptance}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <Figure label="Sumber data" value={`${summary.sources} berkas`} />
            <Figure label="Baris penjualan" value={summary.sales_rows} />
            <Figure label="Jumlah faktur" value={summary.invoices} />
            <Figure label="Jumlah pelanggan" value={summary.customers} />
            <Figure label="Penjualan bruto" value={rupiah(summary.gross_sales)} />
            <Figure label="Retur" value={rupiah(summary.returns)} />
            <Figure label="Penjualan neto" value={rupiah(summary.net_sales)} />
            <Figure label="PPN menurut penjualan" value={rupiah(summary.vat)} />
            <Figure label="Kelengkapan periode" value={summary.coverage} status={summary.coverage_status} />
            <Figure label="Baris di luar periode" value={summary.out_of_period_rows} />
            <Figure label="Pendapatan buku besar" value={rupiah(summary.revenue_gl)} />
            <Figure label="Pemetaan akun" value={summary.revenue_mapping_status} status={summary.revenue_mapping_status} />
            <Figure label="Penjualan ↔ GL" value={statusText(summary.sales_vs_gl_status)} status={summary.sales_vs_gl_status} />
            <Figure label="Penjualan ↔ PPN" value={statusText(summary.sales_vs_vat_status)} status={summary.sales_vs_vat_status} />
            <Figure label="Penjualan ↔ Piutang" value={statusText(summary.sales_vs_ar_status)} status={summary.sales_vs_ar_status} />
            <Figure label="Piutang akhir" value={summary.ar_closing ? rupiah(summary.ar_closing) : NOT_AVAILABLE} />
            <Figure label="Pos cut-off" value={summary.cutoff_exceptions} />
            <Figure label="Penjualan belum cocok" value={summary.unmatched_sales ?? "—"} />
            <Figure label="Buku besar belum cocok" value={summary.unmatched_gl ?? "—"} />
            <Figure label="PPN belum cocok" value={summary.unmatched_vat ?? "—"} />
          </div>
        </div>
      )}

      <div className="bg-white border border-border rounded-sm">
        <div className="flex flex-wrap gap-1 border-b border-border px-2 py-1">
          {available.map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`sales-tab-${key}`}
              className={`text-xs px-2 py-1 rounded-sm ${tab === key ? "bg-slate-200 font-medium" : "hover:bg-slate-100"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto p-2" data-testid={`sales-panel-${tab}`}>
          {tab === "ringkasan" && <Sources rows={view.sources} mapping={view.revenue_mapping} />}
          {tab === "populasi" && <Population population={view.population} controls={controls} />}
          {tab === "gl" && <Allocations rows={view.sales_vs_gl?.allocations} />}
          {tab === "vsgl" && <Items rows={view.sales_vs_gl?.sales_items} origin="vsgl" />}
          {tab === "vsvat" && <VatPairs result={view.sales_vs_vat} control={view.vat_control} />}
          {tab === "vsar" && <Receivable result={view.sales_vs_ar} tie={view.ar_tie_out} />}
          {tab === "pos" && <Items rows={unresolved(view)} origin="pos" />}
          {tab === "bulanan" && <MonthlyBridge bridge={view.monthly_bridge} lumpsum={view.lumpsum} sourceLabel="Penjualan" sourceKey="sales" prefix="sales" />}
          {tab === "cutoff" && <Cutoff result={view.cutoff} windows={view.cutoff_windows} />}
          {tab === "retur" && <Returns result={view.returns} />}
          {tab === "bukti" && <Evidence rows={view.evidence} />}
        </div>
      </div>
      {glSummary && glSummary.status !== "RECONCILED" && (
        <div className="text-xs text-muted-foreground px-1" data-testid="sales-review-note">
          Terdapat pos yang belum cocok; simpulan audit tetap ditulis oleh auditor.
        </div>
      )}
    </div>
  );
}

function statusText(status) {
  if (status === "SOURCE_NOT_SUPPLIED") return NOT_AVAILABLE;
  return ITEM_LABELS[status] || status;
}

function unresolved(view) {
  const sales = (view.sales_vs_gl?.sales_items || []).filter((r) =>
    ["SALES_ONLY", "AMOUNT_DIFFERENCE", "REVIEW_REQUIRED"].includes(r.status));
  const gl = (view.sales_vs_gl?.gl_items || []).filter((r) =>
    ["GL_ONLY", "AMOUNT_DIFFERENCE", "REVIEW_REQUIRED"].includes(r.status));
  return sales.concat(gl);
}

function Figure({ label, value, status }) {
  return (
    <div className="border border-border rounded-sm p-2">
      <div className="text-muted-foreground">{label}</div>
      <div className={`font-medium tabular-nums ${status ? `inline-block mt-1 px-2 py-0.5 border rounded-sm ${STATUS_STYLE[status] || ""}` : ""}`}>
        {value ?? "—"}
      </div>
    </div>
  );
}

function Sources({ rows = [], mapping }) {
  return (
    <div className="space-y-3">
      <table className="w-full audit-table text-xs" data-testid="sales-sources">
        <thead><tr><th>Berkas</th><th>Sheet</th><th>Jenis sumber</th><th className="text-right">Baris</th>
          <th>Periode sumber</th><th>Catatan</th></tr></thead>
        <tbody>{rows.map((row) => (
          <tr key={row.id}>
            <td>{row.filename}</td><td>{row.sheet}</td><td>{row.source_type}</td>
            <td className="text-right tabular-nums">{row.rows}</td>
            <td>{row.declared_range?.stated || row.as_of || "—"}</td>
            <td className="max-w-md">{(row.notes || []).join(" ") || "—"}</td>
          </tr>))}
        </tbody>
      </table>
      <div className="text-xs" data-testid="sales-mapping">
        <div className="font-medium mb-1">Pemetaan akun pendapatan</div>
        <div>Akun pendapatan: {(mapping?.revenue_accounts || []).join(", ") || "belum disetujui"}</div>
        <div>Akun kontra (retur/diskon): {(mapping?.contra_accounts || []).join(", ") || "—"}</div>
        <div>Selisih penjualan neto vs buku besar: <span className="tabular-nums">{rupiah(mapping?.difference)}</span></div>
      </div>
    </div>
  );
}

function Population({ population, controls }) {
  return (
    <div className="space-y-2 text-xs" data-testid="sales-population">
      <div>Bulan tercakup: {(population.months || []).join(", ") || "—"} ({population.coverage})</div>
      {(population.missing_months || []).length > 0 && (
        <div className="text-red-700">Bulan belum tersedia: {population.missing_months.join(", ")}</div>
      )}
      <table className="w-full audit-table text-xs">
        <tbody>
          <tr><td>Baris dalam periode audit</td><td className="text-right tabular-nums">{population.counts?.in_period}</td></tr>
          <tr><td>Baris sebelum periode</td><td className="text-right tabular-nums">{population.counts?.before_period}</td></tr>
          <tr><td>Baris setelah periode</td><td className="text-right tabular-nums">{population.counts?.after_period}</td></tr>
          <tr><td>Baris tanpa tanggal</td><td className="text-right tabular-nums">{population.counts?.undated}</td></tr>
          <tr><td>Penjualan bruto</td><td className="text-right tabular-nums">{rupiah(controls.gross)}</td></tr>
          <tr><td>Retur</td><td className="text-right tabular-nums">{rupiah(controls.returns)}</td></tr>
          <tr><td>Penjualan neto</td><td className="text-right tabular-nums">{rupiah(controls.net)}</td></tr>
        </tbody>
      </table>
    </div>
  );
}

function Allocations({ rows = [] }) {
  return (
    <table className="w-full audit-table text-xs" data-testid="sales-allocations">
      <thead><tr><th>Referensi</th><th>Faktur</th><th>Buku besar</th><th className="text-right">Penjualan</th>
        <th className="text-right">Buku besar</th><th className="text-right">Selisih</th><th>Aturan</th><th>Bukti</th></tr></thead>
      <tbody>{rows.slice(0, 200).map((row, index) => (
        <tr key={index}>
          <td>{row.key}</td><td>{(row.sales_ids || []).slice(0, 3).join(", ")}</td>
          <td>{(row.gl_ids || []).slice(0, 3).join(", ")}</td>
          <td className="text-right tabular-nums">{rupiah(row.sales_amount)}</td>
          <td className="text-right tabular-nums">{rupiah(row.gl_amount)}</td>
          <td className="text-right tabular-nums">{rupiah(row.difference)}</td>
          <td>{ITEM_LABELS[row.rule] || row.rule}</td>
          <td>{(row.evidence || []).join(", ")}</td>
        </tr>))}
      </tbody>
    </table>
  );
}

function Items({ rows = [], origin }) {
  if (!rows.length) {
    return <div className="text-xs text-muted-foreground p-2" data-testid={`sales-items-${origin}-empty`}>
      Tidak ada pos pada kategori ini.
    </div>;
  }
  return (
    <table className="w-full audit-table text-xs" data-testid={`sales-items-${origin}`}>
      <thead><tr><th>ID</th><th>Tanggal</th><th>Pelanggan</th><th className="text-right">Jumlah</th>
        <th className="text-right">Sisa</th><th>Status</th><th>Catatan</th><th>Sumber</th></tr></thead>
      <tbody>{rows.slice(0, 200).map((row, index) => (
        <tr key={`${row.id}-${index}`}>
          <td>{row.id}</td><td>{row.date}</td><td>{row.customer}</td>
          <td className="text-right tabular-nums">{rupiah(row.amount)}</td>
          <td className="text-right tabular-nums">{rupiah(row.remaining)}</td>
          <td>{ITEM_LABELS[row.status] || row.status}</td>
          <td className="max-w-sm">{(row.notes || []).join(" ")}</td>
          <td>{row.source?.source_file}</td>
        </tr>))}
      </tbody>
    </table>
  );
}

function VatPairs({ result, control }) {
  if (!result) {
    return <div className="text-xs text-muted-foreground p-2" data-testid="sales-vat-empty">{NOT_AVAILABLE}</div>;
  }
  return (
    <div className="space-y-3">
      <table className="w-full audit-table text-xs" data-testid="sales-vat-control">
        <thead><tr><th>Masa</th><th className="text-right">DPP penjualan</th><th className="text-right">PPN penjualan</th>
          <th className="text-right">DPP PPN keluaran</th><th className="text-right">PPN keluaran</th>
          <th className="text-right">Selisih PPN</th></tr></thead>
        <tbody>{(control?.months || []).map((row) => (
          <tr key={row.month}>
            <td>{row.month}</td>
            <td className="text-right tabular-nums">{rupiah(row.sales_base)}</td>
            <td className="text-right tabular-nums">{rupiah(row.sales_vat)}</td>
            <td className="text-right tabular-nums">{rupiah(row.output_base)}</td>
            <td className="text-right tabular-nums">{rupiah(row.output_vat)}</td>
            <td className="text-right tabular-nums">{rupiah(row.vat_difference)}</td>
          </tr>))}
          {control?.total && (
            <tr className="font-medium">
              <td>Total periode</td>
              <td className="text-right tabular-nums">{rupiah(control.total.sales_base)}</td>
              <td className="text-right tabular-nums">{rupiah(control.total.sales_vat)}</td>
              <td className="text-right tabular-nums">{rupiah(control.total.output_base)}</td>
              <td className="text-right tabular-nums">{rupiah(control.total.output_vat)}</td>
              <td className="text-right tabular-nums">{rupiah(control.total.vat_difference)}</td>
            </tr>)}
        </tbody>
      </table>
      <Items rows={(result.sales_items || []).filter((r) => r.status !== "MATCHED_SALES_VAT")} origin="vat" />
    </div>
  );
}

function Receivable({ result, tie }) {
  if (!result) {
    return <div className="text-xs text-muted-foreground p-2" data-testid="sales-ar-empty">{NOT_AVAILABLE}</div>;
  }
  return (
    <div className="space-y-3 text-xs" data-testid="sales-ar">
      <div>Tingkat data sumber: <span className="font-medium">{result.granularity}</span></div>
      {result.summary?.limitation && (
        <div className="text-amber-800" data-testid="sales-ar-limitation">{result.summary.limitation}</div>
      )}
      {tie && (
        <table className="w-full audit-table text-xs" data-testid="sales-ar-tieout">
          <tbody>
            <tr><td>Umur piutang per tanggal pelaporan</td><td className="text-right tabular-nums">{rupiah(tie.aging_total)}</td></tr>
            <tr><td>Saldo piutang buku besar</td><td className="text-right tabular-nums">{rupiah(tie.gl_total)}</td></tr>
            <tr><td>Selisih</td><td className="text-right tabular-nums">{rupiah(tie.difference)}</td></tr>
          </tbody>
        </table>
      )}
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Pelanggan</th><th className="text-right">Saldo piutang</th>
          <th className="text-right">Faktur penjualan</th><th>Status</th><th>Catatan</th></tr></thead>
        <tbody>{(result.ar_items || []).slice(0, 200).map((row, index) => (
          <tr key={index}>
            <td>{row.customer}</td>
            <td className="text-right tabular-nums">{rupiah(row.outstanding)}</td>
            <td className="text-right tabular-nums">{row.sales_invoices ?? "—"}</td>
            <td>{ITEM_LABELS[row.status] || row.status}</td>
            <td className="max-w-md">{(row.notes || []).join(" ")}</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

function Cutoff({ result, windows }) {
  const items = result?.items || [];
  return (
    <div className="space-y-2 text-xs" data-testid="sales-cutoff">
      <div>
        Jendela reviu:{" "}
        {Object.entries(windows || {}).map(([days, row]) => `±${days} hari: ${row.count}`).join(" · ") || "—"}
      </div>
      <div className="text-muted-foreground">{result?.note}</div>
      {items.length === 0 ? <div className="p-2">Tidak ada pos cut-off pada jendela ini.</div> : (
        <table className="w-full audit-table text-xs">
          <thead><tr><th>Faktur</th><th>Tanggal</th><th>Pelanggan</th><th className="text-right">Jumlah</th>
            <th>Klasifikasi</th><th className="text-right">Hari dari akhir periode</th><th>Simpulan auditor</th></tr></thead>
          <tbody>{items.slice(0, 200).map((row, index) => (
            <tr key={index}>
              <td>{row.invoice_no}</td><td>{row.date}</td><td>{row.customer}</td>
              <td className="text-right tabular-nums">{rupiah(row.amount)}</td>
              <td>{row.cutoff}</td>
              <td className="text-right tabular-nums">{row.days_from_period_end}</td>
              <td className="text-muted-foreground">Belum disimpulkan</td>
            </tr>))}
          </tbody>
        </table>)}
    </div>
  );
}

function Returns({ result }) {
  if (!result || result.status !== "PRESENT") {
    return <div className="text-xs text-muted-foreground p-2" data-testid="sales-returns-empty">
      Sumber tidak memuat retur, diskon atau nota kredit.
    </div>;
  }
  return (
    <table className="w-full audit-table text-xs" data-testid="sales-returns">
      <thead><tr><th>Faktur</th><th>Tanggal</th><th>Pelanggan</th><th className="text-right">Jumlah</th>
        <th>Klasifikasi</th><th>Sumber</th></tr></thead>
      <tbody>{(result.items || []).map((row, index) => (
        <tr key={index}>
          <td>{row.invoice_no}</td><td>{row.date}</td><td>{row.customer}</td>
          <td className="text-right tabular-nums">{rupiah(row.amount)}</td>
          <td>{row.classification}</td><td>{row.source?.source_file}</td>
        </tr>))}
      </tbody>
    </table>
  );
}

function Evidence({ rows = [] }) {
  return (
    <table className="w-full audit-table text-xs" data-testid="sales-evidence">
      <thead><tr><th>Berkas</th><th>Sheet</th><th>Jenis</th><th>SHA-256</th><th>Periode</th><th>Diperoleh</th></tr></thead>
      <tbody>{rows.map((row, index) => (
        <tr key={index}>
          <td>{row.filename}</td><td>{row.sheet}</td><td>{row.source_type}</td>
          <td className="font-mono">{(row.sha256 || "").slice(0, 16)}…</td>
          <td>{row.period || "—"}</td><td>{row.obtained_at}</td>
        </tr>))}
      </tbody>
    </table>
  );
}
